import React, { useState } from 'react';
import { MdWarningAmber, MdOutlineEmail } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { SignupLayout } from '../ui/SignupLayout';
import { formatDate, formatCurrency } from '../util/formatters';

// Map field keys to readable labels for the exposure summary
const EXPOSURE_LABELS = {
    ExposedHome: 'Home',
    ExposedLandImprovements: 'Land/Improvements (sea walls, retaining walls, docks, etc)',
    ExposedHardscaping: 'Hardscaping and landscaping',
    ExposedContents: 'Contents',
    ExposedValuables: 'Valuables (art, collections, etc.)',
    ExposedVehicles: 'Vehicles (cars, golf carts, etc)',
    ExposedLivingExpenses: 'Living expenses if there is damage (housing, food, etc)',
};

export function NotEligible() {
    const { formData } = useFormContext();

    // Build the list of selected exposure items
    const selectedExposures = Object.entries(EXPOSURE_LABELS)
        .filter(([field]) => formData[field])
        .map(([, label]) => label);

    const propertyAddress = [
        formData.TargetAddressOne,
        formData.TargetAddressTwo,
        formData.TargetCity && formData.TargetState
            ? `${formData.TargetCity}, ${formData.TargetState} ${formData.TargetZipCode}`
            : formData.TargetCity || '',
    ]
        .filter(Boolean)
        .join('\n');

    return (
        <SignupLayout currentStep={1} totalSteps={3} stepLabel="Check eligibility">

            {/* ── Ineligible warning card ── */}
            <div className="ineligible-card">
                <div className="ineligible-card__header">
                    <MdWarningAmber size={20} className="ineligible-card__icon" />
                    <strong>Property ineligible for coverage.</strong>
                </div>
                <p className="ineligible-card__intro">
                    Based on the details below, this property does not meet our current coverage criteria.
                </p>

                {/* Property address */}
                <div className="ineligible-row">
                    <span className="ineligible-row__label">Property address</span>
                    <span className="ineligible-row__value" style={{ whiteSpace: 'pre-line' }}>
                        {propertyAddress || '—'}
                    </span>
                </div>

                {/* Coverage start + Property value grid */}
                <div className="ineligible-grid">
                    <div className="ineligible-row">
                        <span className="ineligible-row__label">Coverage start</span>
                        <span className="ineligible-row__value">{formatDate(formData.EffectiveDate)}</span>
                    </div>

                    <div className="ineligible-row">
                        <span className="ineligible-row__label">Coverage Amount</span>
                        <span className="ineligible-row__value">{formatCurrency(formData.CoverageAmount)}</span>
                    </div>
                </div>

                <div className="ineligible-grid">
                    <div className="ineligible-row">
                        <span className="ineligible-row__label">Property value &gt; $1M</span>
                        <span className="ineligible-row__value">
                            {formData.IsExposedValueOverMillion === true
                                ? 'Yes'
                                : formData.IsExposedValueOverMillion === false
                                    ? 'No'
                                    : '—'}
                        </span>
                    </div>
                </div>

                {/* Exposed items */}
                {selectedExposures.length > 0 && (
                    <div className="ineligible-row">
                        <span className="ineligible-row__label">Potential impacted property</span>
                        <span className="ineligible-row__value">
                            {selectedExposures.join(', ')}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Be the first to know ── */}
            <div className="notify-section">
                <h2 className="notify-section__heading">Be the first to know</h2>
                <p className="notify-section__desc">
                    Elevana is actively expanding—we’ll notify you when coverage becomes available in your area.
                </p>

                <p className="notify-section__desc">
                    We’ll email updates to {formData.UserName}
                </p>
            </div>
        </SignupLayout>
    );
}